import { useMemo } from 'react'

const getRandomId = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
}

export const useGetSelectedPageContent = (currentPageIndex, templateContent) => {
  return useMemo(() => {
    if (!templateContent?.pages) return []
    const pageContent = templateContent?.pages?.[currentPageIndex] ?? {}

    const blocks = (pageContent?.blocks?.filter(x => !x.isEmptyBlock) ?? []).map(b => ({
      ...b,
      key: b.key ?? `${b.infographicBlockId}_${getRandomId()}`
    }))

    // find gaps where there are no blocks (rows with no items)
    for (let i = 0; i < 16; i++) {
      const blocksInRange = blocks.filter(block => {
        const blockCords = block.cords ?? {}
        const blockSize = block.size ?? { rows: 1, cols: 12 }
        return blockCords.y <= i && ((blockSize.rows + blockCords.y - 1) >= i)
      })
      if (!blocksInRange?.filter(b => b.size.cols >= 12)?.length) {
        // find empty one that is touching
        const existingEmptyBlockIndex = blocks.findIndex(block => {
          const blockCords = block.cords ?? {}
          const blockSize = block.size ?? { rows: 1, cols: 12 }
          return block.isEmptyBlock &&
            blockCords.y <= i && ((blockSize.rows + blockCords.y - 1) === i)
        })

        if (existingEmptyBlockIndex >= 0) {
          blocks[existingEmptyBlockIndex].size.rows += 1
        } else {
          const totalCols = blocksInRange.reduce((total, block) => total + block.size.cols, 0)

          if (totalCols > 0) {
            const sortedBlocks = [...blocksInRange].sort((a, b) => a.cords.x - b.cords.x)
            let lastX = 0
            sortedBlocks.forEach((block) => {
              if (block.cords.x > lastX) {
                blocks.push({
                  cords: { x: lastX, y: i },
                  size: { cols: block.cords.x - lastX, rows: 1 },
                  infographicBlock: { name: 'Empty block' },
                  key: `EMPTY_${getRandomId()}`,
                  isEmptyBlock: true
                })
              }
              lastX = block.cords.x + block.size.cols
            })
            if (lastX < 12) {
              blocks.push({
                cords: { x: lastX, y: i },
                size: { cols: 12 - lastX, rows: 1 },
                infographicBlock: { name: 'Empty block' },
                key: `EMPTY_${getRandomId()}`,
                isEmptyBlock: true
              })
            }
          } else {
            blocks.push({
              cords: { x: 0, y: i },
              size: { cols: 12, rows: 1 },
              infographicBlock: { name: 'Empty block' },
              key: `EMPTY_${getRandomId()}`,
              isEmptyBlock: true
            })
          }
        }
      }
    }

    // find any empty blocks that are touching
    return blocks.reduce((combinedBlocks, currentBlock) => {
      if (!currentBlock) return combinedBlocks

      const lastCombinedBlock = combinedBlocks[combinedBlocks.length - 1]

      if (!lastCombinedBlock || !currentBlock.isEmptyBlock) {
        combinedBlocks.push(currentBlock)
        return combinedBlocks
      }

      if (lastCombinedBlock.isEmptyBlock &&
        lastCombinedBlock.cords.y + lastCombinedBlock.size.rows === currentBlock.cords.y &&
        lastCombinedBlock.size.cols === currentBlock.size.cols
      ) {
        lastCombinedBlock.size.rows += currentBlock.size.rows
      } else {
        combinedBlocks.push(currentBlock)
      }

      return combinedBlocks
    }, []).sort((a, b) => a.isEmptyBlock - b.isEmptyBlock)
  }, [currentPageIndex, templateContent])
}
